import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAds } from '@xing-com/crate-ads-native-ads';
import { AdTypes } from '@xing-com/crate-common-graphql-types';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { BannerContextProvider } from '@xing-com/crate-jobs-components-banner';
import { JobNotAvailable } from '@xing-com/crate-jobs-domain-job-details-components-error';
import type { SerpsListItem } from '@xing-com/crate-jobs-domain-serps-constants';
import type {
  DescriptionFragment,
  JobPostingSkillFragment,
} from '@xing-com/crate-jobs-graphql';
import type {
  JobDetails,
  UserJobPreferencesData,
} from '@xing-com/crate-jobs-helpers';
import { matchJobWithPreferences } from '@xing-com/crate-jobs-helpers';
import { useSimilarJobs, useJobContacts } from '@xing-com/crate-jobs-hooks';
import {
  AiJobSummary,
  MatchingSkills,
  SearchAlertsJobDetailsModal,
} from '@xing-com/crate-jobs-xinglets';
import { JobDetailsPushNotification as PushNotificationDialog } from '@xing-com/crate-notifications-xinglets';
import { XingletLoader } from '@xing-com/crate-xinglet';
import { GridContainer, GridItem } from '@xing-com/grid';
import { useExperiment } from '@xing-com/hub';

import { useModulesVisibility } from '../../hooks';
import { CompanyContactsModule } from '../company-contacts-module';
import { CompanyModule } from '../company-module';
import { DescriptionModule } from '../description-module';
import { ExternalJobBanner } from '../external-job-banner';
import { Header } from '../header';
import { JobMetadata } from '../job-metadata';
import { LinkboxesModule } from '../linkboxes-module';
import { SalaryModule } from '../salary-module';
import { SearchAlertModule } from '../search-alert-module';
import { SimilarJobsModule } from '../similar-jobs-module';
import { useJobDetailsTracking } from '../tracking';

import {
  SIMILAR_JOBS_TOP_LIMIT,
  SIMILAR_JOBS_BOTTOM_LIMIT,
  SIMILAR_JOBS_LIMIT,
} from './constants';
import { JobDetailModule } from './job-detail-module';
import {
  Container,
  JobDetailsGridContainer,
  TopBar,
  AiSummaryAndMatchingSkillsWrapper,
} from './job-details-content.styles';
import { orderSimilarJobs } from './order-similar-jobs';

const isValidDescription = (description: DescriptionFragment): boolean => {
  return description.__typename !== 'HtmlDescription' || !!description.content;
};

type Props = {
  jobDetails: JobDetails;
  userJobPreferences?: UserJobPreferencesData;
  matchedSkills?: JobPostingSkillFragment[];
  notMatchedSkills?: JobPostingSkillFragment[];
  linkboxes?: {
    kind: string;
    entries: SerpsListItem[];
  }[];
  jobRole: string;
};

export type GetSimilarJobAdsNumberType = (params: {
  showSimilarJobAds: boolean;
  isAdShown: boolean;
}) => {
  bottomJobAdsSlice: number[];
  topJobAdsSlice: number[];
};

export const getSimilarJobAdsNumber: GetSimilarJobAdsNumberType = ({
  showSimilarJobAds,
  isAdShown,
}) => {
  // only show bottom similar job ads
  if (!showSimilarJobAds) {
    return {
      topJobAdsSlice: [0, 0],
      bottomJobAdsSlice: [0, SIMILAR_JOBS_BOTTOM_LIMIT],
    };
  }

  return {
    topJobAdsSlice: [
      0,
      isAdShown ? SIMILAR_JOBS_TOP_LIMIT - 1 : SIMILAR_JOBS_TOP_LIMIT,
    ],
    bottomJobAdsSlice: [
      isAdShown ? SIMILAR_JOBS_TOP_LIMIT - 1 : SIMILAR_JOBS_TOP_LIMIT,
      SIMILAR_JOBS_LIMIT,
    ],
  };
};

export const JobDetailsContent: React.FC<Props> = ({
  jobDetails,
  userJobPreferences,
  matchedSkills,
  notMatchedSkills,
  linkboxes,
  jobRole,
}) => {
  const { formatMessage } = useIntl();
  const { isLoggedIn } = useLoginState();
  const isTopSimilarJobsAdShown = useExperiment('ABACUS-514') === 'B';
  const {
    id: jobId,
    slug,
    application,
    company,
    paid,
    searchAlertFilters,
    salary,
    description,
    jobCode,
    globalId,
    redirectsToThirdPartyUrl,
    summary,
  } = jobDetails;
  const showSimilarJobAds = !paid;

  const skipFetchAds =
    !isLoggedIn || !showSimilarJobAds || !isTopSimilarJobsAdShown;
  const { ads, isCompleted: isAdFetched } = useAds({
    adCount: 1,
    adTypes: [AdTypes.WebsiteAd],
    channel: 'job_details_page_web',
    // we should skip query if:
    // - the user is a paying user
    // - if the user is not logged in
    // - if the experiment ABACUS-514 is not active (!= "B")
    skipQuery: skipFetchAds,
  });

  const showAds = ads.length > 0 && isTopSimilarJobsAdShown;

  const { loading: similarJobsLoading, similarJobs } = useSimilarJobs({
    limit: !showSimilarJobAds ? SIMILAR_JOBS_BOTTOM_LIMIT : SIMILAR_JOBS_LIMIT,
    jobId,
    skip: redirectsToThirdPartyUrl,
  });
  const {
    loading: jobContactsLoading,
    error: jobContactsError,
    contacts: jobContacts,
    hasMoreContacts: hasMoreJobContacts,
  } = useJobContacts({
    jobId,
    consumer: 'loggedin.web.jobs.posting.contacts.module',
    sourceEntityId: company?.id,
    skip: redirectsToThirdPartyUrl,
  });
  const orderedSimilarJobs = useMemo(
    () =>
      orderSimilarJobs({
        similarJobs,
      }),
    [similarJobs]
  );
  const hasJobContacts =
    !jobContactsLoading && !jobContactsError && jobContacts.length > 0;
  const isValidJob = isValidDescription(description);
  const { bottomJobAdsSlice, topJobAdsSlice } = getSimilarJobAdsNumber({
    showSimilarJobAds,
    isAdShown: showAds,
  });
  const similarTopJobAds = orderedSimilarJobs.slice(...topJobAdsSlice);
  const similarBottomJobAds = orderedSimilarJobs.slice(...bottomJobAdsSlice);

  const matchedJobPreferences = useMemo(
    () =>
      userJobPreferences
        ? matchJobWithPreferences(jobDetails, userJobPreferences)
        : [],
    [jobDetails, userJobPreferences]
  );
  const hasMatchingData =
    !!matchedSkills?.length ||
    !!notMatchedSkills?.length ||
    !!matchedJobPreferences.length;

  const { visibleModules } = useModulesVisibility(
    jobDetails,
    hasMatchingData,
    linkboxes
  );

  useJobDetailsTracking({
    job: jobDetails,
    matchedJobPreferences,
    matchedSkills,
    skip: !isValidJob,
  });

  if (!isValidJob) {
    return <JobNotAvailable />;
  }

  const title = formatMessage({ id: 'JOBS_JOB_DETAILS_TOPBAR_TITLE' });

  return (
    <BannerContextProvider disableFocusTrap>
      {redirectsToThirdPartyUrl ? null : (
        <PushNotificationDialog jobId={jobId} />
      )}
      <JobMetadata job={jobDetails} />
      {isLoggedIn ? <TopBar type="subheader" title={title} /> : null}
      <Container
        $isScrollFixed={redirectsToThirdPartyUrl}
        $isLiUser={isLoggedIn}
      >
        <Header
          isSimilarJobsLoading={similarJobsLoading}
          isSimilarJobsVisible={showSimilarJobAds && visibleModules.SimilarJobs}
          job={jobDetails}
          similarJobs={similarTopJobAds}
          ads={showAds ? ads : []}
          isLoadingAds={!skipFetchAds && !isAdFetched}
        />
        <JobDetailsGridContainer>
          <JobDetailModule visible={visibleModules.MatchingBanner}>
            <XingletLoader
              name="@xing-com/crate-jobs-domain-job-details-lo-matching-banner"
              globalId={globalId}
            />
          </JobDetailModule>
          {visibleModules.AiSummary || visibleModules.MatchingSkills ? (
            <AiSummaryAndMatchingSkillsWrapper>
              <JobDetailModule visible={visibleModules.AiSummary}>
                <AiJobSummary jobGlobalId={globalId} summary={summary} />
              </JobDetailModule>
              <JobDetailModule visible={visibleModules.MatchingSkills}>
                <MatchingSkills
                  job={jobDetails}
                  matchedSkills={matchedSkills}
                  notMatchedSkills={notMatchedSkills}
                  matchedPreferences={matchedJobPreferences}
                />
              </JobDetailModule>
            </AiSummaryAndMatchingSkillsWrapper>
          ) : null}
          {redirectsToThirdPartyUrl && application.applyUrl ? (
            <ExternalJobBanner url={application.applyUrl} jobId={jobId} />
          ) : null}
          <GridItem span={12}>
            <GridContainer variant="FullSize">
              <JobDetailModule
                spanLarge={12}
                visible={visibleModules.Description}
              >
                <DescriptionModule
                  jobId={jobId}
                  description={description}
                  jobCode={jobCode}
                  isThirdParty={redirectsToThirdPartyUrl}
                />
              </JobDetailModule>
            </GridContainer>
          </GridItem>
          <JobDetailModule visible={visibleModules.Salary}>
            <SalaryModule salary={salary} />
          </JobDetailModule>
          <JobDetailModule visible={visibleModules.Company}>
            <CompanyModule jobSlug={slug} company={company} jobId={jobId} />
          </JobDetailModule>
          <JobDetailModule visible={visibleModules.Contacts && hasJobContacts}>
            <CompanyContactsModule
              jobGlobalId={globalId}
              jobSlug={slug}
              contacts={jobContacts}
              hasMoreContacts={hasMoreJobContacts}
              company={company}
            />
          </JobDetailModule>
          <JobDetailModule visible={visibleModules.SearchAlerts}>
            <SearchAlertModule
              searchAlertFilters={searchAlertFilters}
              isThirdPartyJob={redirectsToThirdPartyUrl}
            />
          </JobDetailModule>
          <JobDetailModule visible={visibleModules.SimilarJobs}>
            <SimilarJobsModule
              isLoading={similarJobsLoading}
              similarJobs={similarBottomJobAds}
            />
          </JobDetailModule>
          <JobDetailModule visible={visibleModules.Linkboxes}>
            <LinkboxesModule
              jobDetails={jobDetails}
              linkboxes={linkboxes}
              jobRole={jobRole}
            />
          </JobDetailModule>
        </JobDetailsGridContainer>
      </Container>
      <SearchAlertsJobDetailsModal isThirdParty={redirectsToThirdPartyUrl} />
    </BannerContextProvider>
  );
};
